import qs from 'qs';
import mitt from 'mitt';
import { useIsStandalone } from '@/composables/standalone';
import { useRuntimeConfig } from '#imports';
import {
  STORAGE_KEY_UID,
  STORAGE_KEY_TOKEN,
  STORAGE_KEY_REFRESH,
  STORAGE_KEY_API,
} from '../constants/sdk';

export function isPlainObject (obj) {
  if (obj === null || typeof obj !== 'object') return false;
  return Object.getPrototypeOf(obj) === Object.prototype;
}

export function mapValues (obj, mapperFn) {
  if (!obj) return obj;
  return Object.keys(obj).reduce((acc, key) => ({ ...acc, [key]: mapperFn(obj[key], key) }), {});
}

function parseJWTPayload (token) {
  try {
    let payloadPart = token?.split('.')?.[1];
    // Replace non-url compatible chars with base64 standard chars
    payloadPart = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
    // Pad out with standard base64 required padding characters
    const pad = payloadPart.length % 4;
    if (pad) {
      if (pad === 1) throw new Error('InvalidLengthError: Input base64url string is the wrong length to determine padding');
      payloadPart += new Array(5 - pad).join('=');
    }
    return JSON.parse(window.atob(payloadPart));
  } catch (error) {
    error.message = 'Failed to parse JWT. ' + error.message;
    throw error;
  }
}

function encodeValues (obj) {
  // array
  if (Array.isArray(obj)) {
    // empty array
    if (!obj.length) return '#[]';
    // iterate list
    return obj.map(encodeValues);
  }

  // boolean
  if (typeof obj === 'boolean') {
    return `#${obj}`;
  }

  // encode numbers
  if (typeof obj === 'number') {
    return `#${obj}`;
  }

  // deep encoding
  if (isPlainObject(obj)) {
    return mapValues(obj, encodeValues);
  }

  // no encoding
  return obj;
}

export class Sdk {
  /**
   * @param {string} apiBaseUrl
   * @param {object} opts
   */
  constructor (apiBaseUrl, opts) {
    Object.defineProperty(this, '_emitter', { value: mitt(), enumerable: false });
    this.apiBaseUrl = apiBaseUrl;
    this.log = opts?.log || console.log.bind(console);
    this.currentUser$ = null;
    this.storage = Object.assign({
      getItem: (key) => globalThis.localStorage.getItem(key),
      setItem: (key, value) => globalThis.localStorage.setItem(key, value),
      removeItem: (key) => globalThis.localStorage.removeItem(key),
    }, opts.storage);
  }

  /**
   * Register an event handler
   *
   * @param {string} event
   * @param {function} handler
   * @returns {function} unsubscribe
   */
  on (event, handler) {
    return this._emitter.on(event, handler);
  }

  /**
   * Unregister an event handler
   *
   * @param {string} event
   * @param {function} handler
   * @returns {void}
   */
  off (event, handler) {
    return this._emitter.off(event, handler);
  }

  /**
   * Emit an event
   *
   * @param {string} event
   * @param  {...any} args
   * @returns {void}
   */
  emit (event, ...args) {
    return this._emitter.emit(event, ...args);
  }

  async setBaseUrl (apiBaseUrl) {
    apiBaseUrl = apiBaseUrl || useSdkConstants(apiBaseUrl).apiBaseUrl;

    if (!apiBaseUrl) throw new Error('Api Base Url required');
    if (this.apiBaseUrl === apiBaseUrl) return;
    this.storage.setItem(STORAGE_KEY_API, apiBaseUrl);
    this.apiBaseUrl = apiBaseUrl;
    this.connectWs();
    this.log('base url reset to', this.apiBaseUrl);
  }

  async currentUser (forceFetch = false) {
    // return cached currentUser
    if (!forceFetch && this.currentUser$) {
      return this.currentUser$;
    }

    // check if there is a cached access token
    const accessToken = await this.token().catch(error => {
      // handle network errors
      switch (error.message) {
        case 'Failed to fetch':
        case 'NetworkError when attempting to fetch resource.':
        case 'Network request failed': {
          throw error;
        }
      }

      // signout expired jwt error
      this.log('invalid token, signing out.', error);
      return this.signout();
    });

    if (!accessToken) {
      this.log('no cached access token');
      return null;
    }

    // signin with the token
    return this.signinWithToken(accessToken);
  }

  async token () {
    const accessToken = this.storage.getItem(STORAGE_KEY_TOKEN);
    if (!accessToken) return;
    const refreshToken = this.storage.getItem(STORAGE_KEY_REFRESH);
    const expiresIn = parseJWTPayload(accessToken).exp;

    // no refresh/expiration, let the server check
    if (!refreshToken || !expiresIn) return accessToken;

    // not yet expired
    if ((Date.now() / 1000) < expiresIn) return accessToken;

    // refresh the token
    this.log('token expired. refreshing');
    const body = { action: 'refreshAccessToken', refreshToken };
    try {
      const creds = await this.create('authentication', body, { skipAuth: true });
      if (!creds?.accessToken) throw new Error('Invalid refresh response. missing accessToken');
      if (!creds?.refreshToken) throw new Error('Invalid refresh response. missing refreshToken');
      this.storage.setItem(STORAGE_KEY_TOKEN, creds.accessToken);
      this.storage.setItem(STORAGE_KEY_REFRESH, creds.refreshToken);

      this.log('token refreshed');
      return creds.accessToken;
    } catch (error) {
      this.log('failed to refresh token', error);
      await this.signout();
    }
  }

  async signinWithEmail (email, password, opts = {}) {
    if (!email) throw new Error('email is required');
    if (!password) throw new Error('password is required');
    this.log(`signing-in with email ${email}`);

    const body = { strategy: 'local', email, password };
    if (opts.remoteApiUrl) {
      body.remoteApiUrl = opts.remoteApiUrl;
      body.remoteSyncForce = opts.remoteSyncForce;
    }
    // Add OTP token if provided
    if (opts.totpToken) {
      body.totpToken = opts.totpToken;
    }
    
    // Need to handle 206 response for OTP requirement
    const requestOpts = { skipAuth: true, throwHttpErrors: false, returnBodyOnly: false };
    const response = await this.create('authentication', body, requestOpts);
    
    // Check if OTP is required (206 response)
    if (response.statusCode === 206) {
      const error = new Error('OTP_REQUIRED');
      error.statusCode = 206;
      error.data = response.data;
      throw error;
    }
    
    // Handle other non-success status codes
    if (response.statusCode >= 400) {
      const error = new Error(response.data?.message || `Authentication failed with status ${response.statusCode}`);
      error.statusCode = response.statusCode;
      error.data = response.data;
      throw error;
    }

    return this.loadUser(response.data);
  }

  async signinWithToken (token) {
    if (!token) throw new Error('token is required');
    this.log('signing-in with token');

    const body = { strategy: 'jwt', accessToken: token };
    const creds = await this.create('authentication', body, { skipAuth: true });

    return this.loadUser(creds);
  }

  async loadUser (creds) {
    if (!creds?.uid) throw new Error('Invalid authenticated response. missing uid');
    if (!creds?.accessToken) throw new Error('Invalid authentication response. missing accessToken');
    if (!creds?.user?.uid) throw new Error('Invalid authenticated response. missing populated user');

    // save tokens
    this.log('saving tokens');
    this.storage.setItem(STORAGE_KEY_UID, creds.uid);
    this.storage.setItem(STORAGE_KEY_TOKEN, creds.accessToken);
    this.storage.setItem(STORAGE_KEY_REFRESH, creds.refreshToken);

    // fetch account details
    this.log('fetching user details');
    const user = creds.user;
    this.currentUser$ = user;

    this.connectWs();

    return user;
  }

  async signout () {
    this.storage.removeItem(STORAGE_KEY_UID);
    this.storage.removeItem(STORAGE_KEY_TOKEN);
    this.storage.removeItem(STORAGE_KEY_REFRESH);
    this.currentUser$ = null;
    this.connectWs();
    this.log('signed out');

    await this.setBaseUrl(null, { skipSignout: true });
  }

  async connectWs () {
    const token = await this.token();

    // no change in token
    if (this.ws && token === this.prevToken) {
      this.log('ws: no change in token');
      return;
    }
    this.prevToken = token;

    // close existing ws
    const prevWs = this.ws;
    this.ws = null;
    if (prevWs) prevWs.close();

    // connect to realtime
    const wssurl = new URL(this.apiBaseUrl.replace('http', 'ws') + '/ws');
    if (token) wssurl.searchParams.set('token', token);
    this.log(`connecting to realtime via websocket: ${wssurl.origin}`);
    const ws = this.ws = new globalThis.WebSocket(wssurl);
    ws._status = 'CONNECTING';

    ws.onopen = () => {
      ws._status = 'OPEN';
      this.log(`ws connected to ${wssurl.origin}`);
    };
    ws.onclose = (ev) => {
      ws._status = 'CLOSED';
      this.ws = null;
      const delay = 1000;
      this.log(`ws closed. reconnecting after ${delay}ms`, ev);
      setTimeout(this.connectWs.bind(this), delay);
    };
    ws.onmessage = (ev) => {
      const message = JSON.parse(ev.data);
      // this.log('ws onmessage', ev, message);
      this.emit('ws:message', message);
    };
  }

  onWsMessage (path, opts, handler) {
    if (!path) throw new Error('path is required');
    if (typeof opts === 'function') {
      handler = opts;
      opts = {};
    }
    if (typeof handler !== 'function') throw new Error('handler is required');
    opts = Object.assign({
      resolveDataOnly: true,
    }, opts);
    const onMessage = message => {
      if (message.path !== path) return;
      if (opts?.event && message.event !== opts.event) return;
      const result = opts.resolveDataOnly ? message.data : message;
      handler(result);
    };
    this.on('ws:message', onMessage);
    return () => this.off('ws:message', onMessage);
  }

  /**
   * @param {string} input
   * @param {object} opts
   * @param {string} opts.method
   * @param {string} opts.prefixUrl
   * @param {string} opts.responseType json (default) | text
   * @param {object} opts.headers
   * @param {object} opts.data JSON body
   * @param {object} opts.searchParams query params in object notation
   * @param {boolean} opts.throwHttpErrors defaults to true. throws on 200 > x > 299
   * @param {boolean} opts.returnBodyOnly returns only the parsed body
   * @returns {object}
   */
  async request (input, opts) {
    // handle opts
    opts = Object.assign({
      method: 'GET',
      prefixUrl: this.apiBaseUrl,
      responseType: 'json',
      throwHttpErrors: true,
      returnBodyOnly: false,
    }, opts);

    // build request options
    const req = {
      url: [opts.prefixUrl, input].filter(Boolean).join('/'),
      method: opts.method.toUpperCase(),
      headers: opts.headers || {},
    };

    // attach body
    if (opts.data) {
      req.body = JSON.stringify(opts.data);
      req.headers['Content-Type'] = 'application/json';
    }

    // build query string
    if (opts.searchParams) {
      req.searchParams = opts.searchParams;
      req.searchParamsStringified = qs.stringify(encodeValues(opts.searchParams), { strictNullHandling: true });
      req.url += `?${req.searchParamsStringified}`;
    }

    // auto authentication token attachment
    if (!opts.skipAuth) {
      const token = await this.token();
      if (token) {
        req.headers.Authorization = `Bearer ${token}`;
      }
    }

    try {
      // this.log('request.req', req);
      const res = await globalThis.fetch(req.url, {
        method: req.method,
        headers: req.headers,
        body: req.body,
      });
      const resobj = {
        statusCode: res.status,
        statusText: res.statusText,
        responseType: opts.responseType,
      };
      Object.defineProperty(resobj, 'req', {
        enumerable: false,
        writable: false,
        value: req,
      });
      Object.defineProperty(resobj, 'res', {
        enumerable: false,
        writable: false,
        value: res,
      });

      // parse body
      switch (opts.responseType) {
        case 'json': {
          resobj.data = res.status === 204 ? null : await res.json();
          break;
        }
        default: {
          resobj.data = await res.text();
        }
      }

      // handle http error
      if (!res.ok && opts.throwHttpErrors) {
        const error = new Error(resobj.data?.message || `Request failed with status ${resobj.statusText || resobj.statusCode}`);
        error.res = resobj;
        error.name = 'HttpError';
        error.type = resobj.data?.type;
        error.code = resobj.data?.code;
        error.errors = resobj.data?.errors;
        
        // Capture API errors to Sentry
        if (typeof window !== 'undefined' && window.$nuxt?.$sentry) {
          window.$nuxt.$sentry.captureException(error, {
            tags: {
              api_error: true,
              status_code: resobj.statusCode,
              api_endpoint: input,
              http_method: req.method,
            },
            extra: {
              request_url: req.url,
              request_body: opts.data,
              response_status: resobj.statusCode,
              response_body: resobj.data,
              error_type: error.type,
              error_code: error.code,
            },
            fingerprint: [`api-error-${resobj.statusCode}`, input, req.method],
          });
        }
        
        throw error;
      }

      // this.log('request.res', resobj);
      return opts.returnBodyOnly ? resobj.data : resobj;
    } catch (error) {
      // Capture network errors to Sentry
      if (error.name !== 'HttpError' && typeof window !== 'undefined' && window.$nuxt?.$sentry) {
        window.$nuxt.$sentry.captureException(error, {
          tags: {
            network_error: true,
            api_endpoint: input,
            http_method: req.method,
          },
          extra: {
            request_url: req.url,
            request_body: opts.data,
            error_message: error.message,
          },
          fingerprint: ['network-error', input, req.method],
        });
      }
      throw error;
    }
  }

  async list (input, query, opts) {
    opts = Object.assign({
      method: 'GET',
      returnBodyOnly: true,
    }, opts);

    // NOTE: handle backwards compat
    if (query?.$page && query?.$limit) {
      query.$skip = query.$limit * (query.$page - 1);
      delete query.$page;
    }

    if (query) opts.searchParams = query;
    const res = await this.request(input, opts);

    // NOTE: handle backwards compat
    const result = {};
    result.data = Array.isArray(res) ? res : res.data;
    result.total = Array.isArray(res) ? undefined : res.total;
    result.limit = Array.isArray(res) ? undefined : res.limit;
    result.skip = Array.isArray(res) ? undefined : res.skip;
    return result;
  }

  async get (input, query, opts) {
    opts = Object.assign({
      method: 'GET',
      returnBodyOnly: true,
    }, opts);
    if (typeof query === 'string') {
      input = [input, query].join('/');
    } else if (query) {
      opts.searchParams = query;
    }
    const res = await this.request(input, opts);
    return Array.isArray(res?.data) ? res.data[0] : res;
  }

  async create (input, data, opts) {
    opts = Object.assign({
      method: 'POST',
      returnBodyOnly: true,
    }, opts);
    if (data) {
      opts.data = data;
    }
    return this.request(input, opts);
  }

  async update (input, query, data, opts) {
    opts = Object.assign({
      method: 'PATCH',
      returnBodyOnly: true,
    }, opts);
    if (typeof query === 'string') {
      input = [input, query].join('/');
    } else if (query) {
      opts.searchParams = query;
    }
    if (data) {
      opts.data = data;
    }
    return this.request(input, opts);
  }

  async delete (input, query, opts) {
    opts = Object.assign({
      method: 'DELETE',
      returnBodyOnly: true,
    }, opts);
    if (typeof query === 'string') {
      input = [input, query].join('/');
    } else if (query) {
      opts.searchParams = query;
    }
    return this.request(input, opts);
  }
}

export function useSdkConstants (apiUrl) {
  const isStandalone = useIsStandalone();

  // all available static urls
  let apiBaseUrls = [];

  // for standalone, fixed url
  if (isStandalone.value) {
    apiBaseUrls = ['http://localhost:7500'];
    const apiBaseUrl = apiBaseUrls[0];
    return {
      apiBaseUrls,
      apiBaseUrl,
    };
  }

  if (apiUrl) {
    apiBaseUrls = [
      // ...Cookies.get(COOKIE_KEY_API_BASE_URL)?.split(',') || [],
      ...apiUrl.split(','),
    ].filter(Boolean);
  }

  // retrieve cached url
  const cachedApiUrl = globalThis.localStorage?.getItem(STORAGE_KEY_API);

  // default url (cached has higher priority)
  const apiBaseUrl = cachedApiUrl?.startsWith('http') ? cachedApiUrl : apiBaseUrls[0];
  // console.info('cookies', Cookies.getAll());
  // console.info(`cookies.${COOKIE_KEY_API_BASE_URL}`, Cookies.get(COOKIE_KEY_API_BASE_URL));
  console.info('apiBaseUrl cached', cachedApiUrl);
  console.info('apiBaseUrl candidates', apiBaseUrls);
  console.info('apiBaseUrl', apiBaseUrl);

  return {
    apiBaseUrls,
    apiBaseUrl,
  };
}

export function useSdk () {
  if (useSdk._sdk) return useSdk._sdk;

  const runtimeConfig = useRuntimeConfig();
  const apiUrl = runtimeConfig.public.apiUrl;
  const { apiBaseUrl } = useSdkConstants(apiUrl);
  if (import.meta.client) globalThis.localStorage.setItem(STORAGE_KEY_API, apiBaseUrl);
  const log = (...args) => {
    if (!globalThis.localStorage.getItem('debug')) return;
    return console.log(...args);
  };
  const sdkopts = { log };

  // handle non-client environments
  if (!import.meta.client) {
    const fakemem = new Map();
    sdkopts.log = (...args) => console.log(...args);
    sdkopts.storage = {
      getItem: (key) => fakemem.get(key),
      setItem: (key, value) => fakemem.set(key, value),
      removeItem: (key) => fakemem.delete(key),
    };
  }
  console.info('initializing sdk');
  const sdk = new Sdk(apiBaseUrl, sdkopts);
  useSdk._sdk = sdk;
  return sdk;
}
